import { useMemo } from 'react'

import {
  Controller,
  ControllerRenderProps,
  FormProvider,
  useForm,
} from 'react-hook-form'

import { Poll, PollStatus } from '@tribeplatform/react-sdk/types'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Checkbox } from '@tribeplatform/react-ui-kit/Checkbox'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { List } from '@tribeplatform/react-ui-kit/Layout'

import { cn } from '../Composer/helpers/cn.js'
import { T, useI18n } from '../i18n/index.js'

import { OnVote } from './types.js'

export function PollOptions({
  poll,
  onVote,
  interactive,
  isLoading,
}: {
  poll: Poll
  onVote: OnVote
  interactive: boolean
  isLoading: boolean
}) {
  const { $t } = useI18n()
  const { options } = poll

  const form = useForm({
    defaultValues: {
      selections: [],
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const onSubmit = form.handleSubmit(data => {
    onVote(data.selections)
  })

  const isDisabled = useMemo(() => {
    const userSelections = poll.usersVote?.selectionIds
    if (!interactive) {
      return true
    }

    if (userSelections?.length) {
      return true
    }

    if (poll.status !== PollStatus.Active) {
      return true
    }

    return false
  }, [interactive, poll])

  const handleSelection = (
    id: number,
    field: ControllerRenderProps<{ selections: number[] }, 'selections'>,
  ) => {
    if (isDisabled) {
      return
    }
    if (poll.maxSelections === 1) {
      field.onChange([id])
    } else if (field.value.includes(id)) {
      field.onChange(field.value.filter(selection => selection !== id))
    } else {
      field.onChange([...field.value, id])
    }
  }

  return (
    <FormProvider {...form}>
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <List className="!p-0 !m-0 gap-2">
          {options.map(({ id, label }) => {
            return (
              <Controller
                key={`${id}-${label}`}
                control={form.control}
                name="selections"
                rules={{
                  validate: {
                    min: value => {
                      if (value.length < 1) {
                        return $t({
                          id: 'Poll.PleaseSelectAtLeastOneOption',
                          defaultMessage: 'Please select at least one option',
                        })
                      }
                      return true
                    },
                    max: value => {
                      if (value.length > poll.maxSelections) {
                        return $t(
                          {
                            id: 'Poll.YouCanOnlySelectUpToOptions',
                            defaultMessage:
                              'You can only select up to {max} options',
                          },
                          {
                            max: poll.maxSelections,
                          },
                        )
                      }
                      return true
                    },
                  },
                }}
                render={({ field }) => (
                  <List.Item
                    className={cn(
                      '!p-4 border border-line-subdued rounded-md relative overflow-hidden flex items-center justify-between',
                      isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    )}
                    onClick={() => handleSelection(id, field)}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        type={poll.maxSelections > 1 ? 'checkbox' : 'radio'}
                        id={`poll-option-${id}`}
                        name="selections"
                        value={id}
                        noLabel
                        checked={field.value.includes(id)}
                        onChange={() => handleSelection(id, field)}
                      />
                      <FormControl.Label htmlFor={`poll-option-${id}`}>
                        {label}
                      </FormControl.Label>
                    </div>
                  </List.Item>
                )}
              />
            )
          })}
        </List>
        <div className="flex items-center justify-end">
          <Button
            className="w-fit px-6"
            type="submit"
            disabled={!form.formState.isValid || isLoading}
            loading={isLoading}
          >
            <T id="Poll.Vote" defaultMessage="Vote" />
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
